import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, signal, ViewChild } from '@angular/core';
import { FieldDef } from '@core/models';
import { TsTableTagsEditor } from '@shared/components/ts-table-renderers/editors/table-tags-editor.component';
import { RelatedToType } from '@shared/enums';
import { DropdownOptionNames, FieldDefsState } from '@state/fields/state';
import {
  CellEditingStoppedEvent,
  ColumnMovedEvent,
  GridOptions,
  GridReadyEvent,
  SortChangedEvent,
  SortDirection,
} from 'ag-grid-enterprise';
import { TsComponent } from 'app/modules/jupiter/@components/ts.component';
import { JupiterState } from 'app/modules/jupiter/@store/state';
import { Entity, TableData } from 'app/modules/jupiter/types';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { TsPaginatorChangeEvent, TsPaginatorModule } from 'tsui';
import { TableActions } from 'tsui/lib/cross-cutting/models/table-actions';
import { TsTableComponent } from 'tsui/lib/ts-table';

@Component({
  selector: 'jupiter-data-table',
  standalone: true,
  imports: [AsyncPipe, TsTableComponent, TsPaginatorModule],
  templateUrl: './data-table.component.html',
  styleUrl: './data-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JupiterDataTableComponent extends TsComponent {
  @Input({ required: true }) data: TableData<any>;
  @Input({ required: true }) set columns(value: string[]) {
    this.setColumnDefinitions(value);
  }
  @Input({ required: true }) activeSort: { active: string; direction: SortDirection };
  @Input() gridActions: TableActions[] = [];
  @Output() pageChanged = new EventEmitter<TsPaginatorChangeEvent>();
  @Output() cellDataChanged = new EventEmitter<Entity>();
  @Output() rowSelectionChanged = new EventEmitter<Entity[]>();
  @Output() columnMoved = new EventEmitter<ColumnMovedEvent>();
  @Output() sortChanged = new EventEmitter<SortChangedEvent>();
  @Output() goToDetails = new EventEmitter<Entity>();

  @ViewChild('tsTable', { static: true }) tsTable: TsTableComponent;

  loading = signal(true);
  columnDefs: any[] = [];
  featureSupport$ = this.store.select(JupiterState.getFeatureSupport);
  redirectConfig$ = this.store.select(ObjectsManagerState.getRedirectConfig);
  options: GridOptions = { onRowDataUpdated: () => this.loading.set(false) };

  constructor() {
    super();
  }

  onOpenDetails(entity: Entity): void {
    this.goToDetails.emit(entity);
  }

  onCellEditingStopped(event: CellEditingStoppedEvent<Entity>): void {
    this.cellDataChanged.emit(event.data);
  }

  onSelectionChanged(selectedRows: Entity[]): void {
    this.rowSelectionChanged.emit(selectedRows);
  }

  onColumnMoved(event: any): void {
    this.columnMoved.emit(event);
  }

  onSortChanged(event: SortChangedEvent): void {
    this.sortChanged.emit(event);
  }

  lookupField(name: string): FieldDef | undefined {
    return this.store
      .selectSnapshot(
        ObjectsManagerState.getFieldsByObjectName(this.store.selectSnapshot(JupiterState.getRelatedTo), {
          allowedFieldsOnly: true,
          addNestedFields: true,
        }),
      )
      .find((fieldDef) => fieldDef.name === name);
  }

  onGridReady(event: GridReadyEvent): void {
    if (this.activeSort) {
      event.api.applyColumnState({
        state: [{ colId: this.activeSort.active, sort: this.activeSort.direction }],
        applyOrder: false,
      });
    }
  }

  onPageChanged(event: TsPaginatorChangeEvent): void {
    this.loading.set(true);
    this.pageChanged.emit(event);
  }

  private setColumnDefinitions(columns: string[]): void {
    this.columnDefs = columns.map((column) => this.lookupField(column)).filter((columnDef) => !!columnDef);
    this.columnDefs.forEach((columnDef) => {
      if (columnDef.meta?.record_type === RelatedToType.User) {
        columnDef.meta.options = this.store
          .selectSnapshot(FieldDefsState.selectDropdownOptionsBy('users' as unknown as DropdownOptionNames))
          .map((x) => ({ id: x.id, label: x.name }));
      }

      columnDef.tsTableOptions = {
        sortable: true,
        resizable: true,
        ...columnDef.tsTableOptions,
        cellEditor: columnDef.type === 'tags' ? TsTableTagsEditor : null,
      };
    });
  }
}
